import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";
import { client } from "@/client";
import { useMutation } from "@apollo/client/react";
import {
  AddPurchaseMutation,
  AddPurchaseMutationVariables,
  AddPurchaseDocument,
  FetchTenderByIdDocument,
  FetchPurchasesOnTenderDocument,
  FetchServicesOnTenderDocument,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for adding a purchase to a tender and handling the necessary cache updates.
 * @param {Object} options - Optional configuration options
 * @param {boolean} [options.disableToast=false] - Whether to disable the success toast
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries
 * @returns {Object} - Contains the addPurchaseMutation function, loading state, and error state.
 */
export default function useAddPurchaseMutationHook(options: { disableToast?: boolean, disableRefetch?: boolean } = {}): { addPurchaseMutation: any, loading: any, error: any } {
  const { disableToast = false, disableRefetch = false } = options;

  // Hooks
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [addPurchaseMutation, { loading, error }] = useMutation<AddPurchaseMutation, AddPurchaseMutationVariables>(
    AddPurchaseDocument, {
      onCompleted() {
        if (!disableToast) {
          toast(CustomToastComponent, {
            toastId: "purchase-added-success",
            data: {
              title: t("toasts.purchaseaddedsuccessfully"),
              type: "success"
            }
          });
        }

        // Refetch only active FetchServicesOnTender queries for this tender
        // This ensures we make only ONE network call instead of multiple
        // for different parameter combinations
        if (!disableRefetch) {
          client.refetchQueries({
            include: [FetchServicesOnTenderDocument],
            onQueryUpdated(observableQuery) {
              // Only refetch queries for this specific tender
              const variables = observableQuery.options.variables as { tenderId?: number } | undefined;
              return variables?.tenderId === Number(tenderID);
            },
          });
        }
      },
      refetchQueries: disableRefetch
        ? []
        : [
          {
            query: FetchTenderByIdDocument,
            variables: { id: Number(tenderID) }
          },
          {
            query: FetchPurchasesOnTenderDocument,
            variables: { tenderId: Number(tenderID) }
          },
        ]
    });

  return {
    addPurchaseMutation,
    loading,
    error
  };
}
